import { Stack, useTheme } from "@mui/material";
import { FileChartColumn } from "lucide-react";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import type {
	ClientPortfolioProducerInstrumentResponseDTO,
	ClientPortfolioToolReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { NormalizedTool } from "web_myplann/Portfolio/Helpers/investmentToolTypes";
import { DataCard } from "web_myplann/Shared/Components/DataCard";
import { ProducerInstrumentRowList } from "./ProducerInstrumentRowList";
import { ToolRowList } from "./ToolRowList";

const PageResources = Resources.PortfolioDetail;

type Props = {
	tools: NormalizedTool[];
	producerInstruments?: ClientPortfolioProducerInstrumentResponseDTO[] | null;
	portfolioId: number;
	title?: string;
	currencySign?: string;
	toolsSummary?: ClientPortfolioToolReportingSummaryResponseDTO[];
	noDivider?: boolean;
};

export const InvestmentTool: FC<Props> = ({
	tools: data,
	producerInstruments,
	portfolioId,
	title,
	currencySign,
	toolsSummary,
	noDivider = false,
}) => {
	const theme = useTheme();
	const { t } = useResource();

	const storageKey = useMemo(
		() => `investmentToolExpanded:${portfolioId}`,
		[portfolioId],
	);

	const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>(
		() => {
			try {
				const stored = sessionStorage.getItem(storageKey);
				return stored ? JSON.parse(stored) : {};
			} catch {
				return {};
			}
		},
	);

	const setExpanded = useCallback(
		(key: string, value: boolean) => {
			setExpandedMap((prev) => {
				const next = { ...prev, [key]: value };
				try {
					sessionStorage.setItem(storageKey, JSON.stringify(next));
				} catch {}
				return next;
			});
		},
		[storageKey],
	);

	useEffect(() => {
		try {
			sessionStorage.setItem(storageKey, JSON.stringify(expandedMap));
		} catch {}
	}, [expandedMap, storageKey]);

	const resolvedCurrencySign = currencySign ?? t(Resources.Common.CurrencySign);

	const hasProducerInstruments =
		producerInstruments && producerInstruments.length > 0;

	return (
		<DataCard
			icon={
				<FileChartColumn
					size={theme.iconSize.sm}
					color={theme.palette.deprecated.grey[700]}
				/>
			}
			label={title ?? t(PageResources.ToolHistory.Commodity.title)}
			noDivider
			isEmpty={!hasProducerInstruments && data.length === 0}
		>
			<Stack>
				{hasProducerInstruments ? (
					<ProducerInstrumentRowList
						producerInstruments={producerInstruments}
						portfolioId={portfolioId}
						currencySign={resolvedCurrencySign}
						expandedMap={expandedMap}
						setExpanded={setExpanded}
						noDivider={noDivider}
					/>
				) : (
					<ToolRowList
						tools={data}
						portfolioId={portfolioId}
						currencySign={resolvedCurrencySign}
						toolsSummary={toolsSummary}
						expandedMap={expandedMap}
						setExpanded={setExpanded}
						noDivider={noDivider}
					/>
				)}
			</Stack>
		</DataCard>
	);
};
